/**
 * ApplicationDetailDrawer — Right-side panel for application details
 * Epic 3B: Broker Application Workflow
 */

import { useState, useEffect, useRef } from 'react'
import {
  X, ChevronRight, Clock, Building2, DollarSign, MapPin, FileText,
  CheckCircle, Circle, Plus, Calendar, AlertTriangle, Save,
  Mail, Phone, Briefcase, Users, Lock,
} from 'lucide-react'
import {
  STAGE_DISPLAY, getValidNextStages, getPhaseColor, getSlaStatus, STAGE_SLA,
} from '../constants/applicationStages'
import {
  getApplicationHistory, getApplicationCommunications,
  addApplicationCondition, clearApplicationCondition,
  getApplicationConditions,
  updateApplicationStage, updateApplicationDetails,
  type Application, type StageHistoryEntry, type ApplicationCondition,
  type CommunicationEntry,
} from '../api/brokerApi'
import {
  getBrokerClientDocuments, getDownloadUrl,
  DOCUMENT_TYPE_LABELS,
  type CustomerDocument, type DocumentType,
} from '../api/documentsApi'
import { getBrokerClientFinancials, type BrokerFinancialSummary } from '../api/incomeExpenseApi'
import { formatNumberWithCommas, stripToNumber } from '../utils/formatCurrency'

interface Props {
  application: Application | null
  token: string
  onClose: () => void
  onStageChanged: () => void
  onApplicationUpdated?: () => void
}

export default function ApplicationDetailDrawer({ application, token, onClose, onStageChanged, onApplicationUpdated }: Props) {
  const [tab, setTab] = useState<'details' | 'notes' | 'conditions' | 'history' | 'comms' | 'documents' | 'financials'>('details')
  const [history, setHistory] = useState<StageHistoryEntry[]>([])
  const [conditions, setConditions] = useState<ApplicationCondition[]>([])
  const [communications, setCommunications] = useState<CommunicationEntry[]>([])
  const [sharedDocs, setSharedDocs] = useState<CustomerDocument[]>([])
  const [docsLoaded, setDocsLoaded] = useState(false)
  const [financials, setFinancials] = useState<BrokerFinancialSummary | null>(null)
  const [financialsLoaded, setFinancialsLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [moveStage, setMoveStage] = useState('')
  const [stageNotes, setStageNotes] = useState('')
  const [newCondition, setNewCondition] = useState('')
  const [brokerNotes, setBrokerNotes] = useState('')
  const [notesSaved, setNotesSaved] = useState(false)
  const [editingDetails, setEditingDetails] = useState(false)
  const [detailForm, setDetailForm] = useState({
    lender_name: '',
    lender_reference: '',
    loan_amount: '',
    property_value: '',
    loan_purpose: '',
    property_address: '',
    settlement_date: '',
    lmi_required: false,
  })
  const [drawerWidth, setDrawerWidth] = useState(512)
  const dragging = useRef(false)

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    dragging.current = true
    const startX = e.clientX
    const startWidth = drawerWidth
    const onMouseMove = (ev: MouseEvent) => {
      if (!dragging.current) return
      const newWidth = startWidth + (startX - ev.clientX)
      setDrawerWidth(Math.max(400, Math.min(newWidth, window.innerWidth * 0.9)))
    }
    const onMouseUp = () => {
      dragging.current = false
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  useEffect(() => {
    if (!application) return
    setTab('details')
    setMoveStage('')
    setStageNotes('')
    setBrokerNotes(application.broker_notes || '')
    setNotesSaved(false)
    setEditingDetails(false)
    setDetailForm({
      lender_name: application.lender_name || '',
      lender_reference: application.lender_reference || '',
      loan_amount: application.loan_amount?.toString() || '',
      property_value: application.property_value?.toString() || '',
      loan_purpose: application.loan_purpose || '',
      property_address: application.property_address || '',
      settlement_date: application.settlement_date || '',
      lmi_required: application.lmi_required,
    })
    loadHistory()
    loadConditions()
  }, [application?.id])

  const loadHistory = async () => {
    if (!application) return
    try {
      const res = await getApplicationHistory(token, application.id)
      setHistory(res.history || [])
    } catch { /* ignore */ }
  }

  const loadConditions = async () => {
    if (!application) return
    try {
      const res = await getApplicationConditions(token, application.id)
      setConditions(res.conditions || [])
    } catch { /* ignore */ }
  }

  const loadCommunications = async () => {
    if (!application) return
    try {
      const res = await getApplicationCommunications(token, application.id)
      setCommunications(res.communications || [])
    } catch { /* ignore */ }
  }

  const loadSharedDocuments = async () => {
    if (!application?.customer_id) return
    try {
      const res = await getBrokerClientDocuments(token, application.customer_id)
      setSharedDocs(res.documents || [])
      setDocsLoaded(true)
    } catch { setSharedDocs([]); setDocsLoaded(true) }
  }

  const loadFinancials = async () => {
    if (!application?.customer_id) return
    try {
      const res = await getBrokerClientFinancials(token, application.customer_id)
      setFinancials(res)
      setFinancialsLoaded(true)
    } catch { setFinancials(null); setFinancialsLoaded(true) }
  }

  const handleDocDownload = async (doc: CustomerDocument) => {
    try {
      const { url } = await getDownloadUrl(token, doc.id)
      window.open(url, '_blank')
    } catch {
      alert('Failed to download document.')
    }
  }

  const handleMoveStage = async () => {
    if (!application || !moveStage) return
    setLoading(true)
    try {
      await updateApplicationStage(token, application.id, moveStage, stageNotes || undefined)
      setMoveStage('')
      setStageNotes('')
      onStageChanged()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to move stage')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveDetails = async () => {
    if (!application) return
    setLoading(true)
    try {
      await updateApplicationDetails(token, application.id, {
        lender_name: detailForm.lender_name || undefined,
        lender_reference: detailForm.lender_reference || undefined,
        loan_amount: detailForm.loan_amount ? parseFloat(detailForm.loan_amount) : undefined,
        property_value: detailForm.property_value ? parseFloat(detailForm.property_value) : undefined,
        loan_purpose: detailForm.loan_purpose || undefined,
        property_address: detailForm.property_address || undefined,
        settlement_date: detailForm.settlement_date || undefined,
        lmi_required: detailForm.lmi_required,
      })
      setEditingDetails(false)
      onApplicationUpdated?.()  // Refresh parent data without closing drawer
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save details')
    } finally {
      setLoading(false)
    }
  }

  const handleAddCondition = async () => {
    if (!application || !newCondition.trim()) return
    try {
      const condition = await addApplicationCondition(token, application.id, newCondition.trim())
      setConditions((prev) => [...prev, condition])
      setNewCondition('')
    } catch { /* ignore */ }
  }

  const handleClearCondition = async (condId: string) => {
    if (!application) return
    try {
      const updated = await clearApplicationCondition(token, application.id, condId)
      setConditions((prev) => prev.map((c) => (c.id === condId ? updated : c)))
    } catch { /* ignore */ }
  }

  const handleSaveNotes = async () => {
    if (!application) return
    setLoading(true)
    try {
      await updateApplicationDetails(token, application.id, { broker_notes: brokerNotes })
      setNotesSaved(true)
      setTimeout(() => setNotesSaved(false), 2000)
      onApplicationUpdated?.()  // Refresh parent data without closing drawer
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save notes')
    } finally {
      setLoading(false)
    }
  }

  if (!application) return null

  const validNext = getValidNextStages(application.stage)
  const phaseColor = getPhaseColor(application.stage)
  const days = application.stage_updated_at
    ? Math.floor((Date.now() - new Date(application.stage_updated_at).getTime()) / 86400000)
    : 0
  const sla = getSlaStatus(application.stage, days)

  const formatCurrency = (v: number | null) =>
    v ? new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(v) : '—'

  return (
    <div
      className="fixed inset-y-0 right-0 bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200"
      style={{ width: `${drawerWidth}px`, maxWidth: '90vw', minWidth: '400px' }}
    >
      {/* Resize handle */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 active:bg-blue-500 z-10"
        onMouseDown={handleMouseDown}
      />
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
            style={{ backgroundColor: phaseColor }}
          >
            {(application.customer_name || 'UN').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 truncate">{application.customer_name || 'Unknown'}</h2>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: phaseColor }}>
              {STAGE_DISPLAY[application.stage] || application.stage}
            </span>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Move Stage */}
      {validNext.length > 0 && (
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
          <label className="text-xs font-medium text-gray-600 block mb-1">Move to Stage</label>
          <div className="flex gap-2">
            <select
              value={moveStage}
              onChange={(e) => setMoveStage(e.target.value)}
              className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Select next stage...</option>
              {validNext.map((s) => (
                <option key={s} value={s}>{STAGE_DISPLAY[s] || s}</option>
              ))}
            </select>
            <button
              onClick={handleMoveStage}
              disabled={!moveStage || loading}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Move
            </button>
          </div>
          {moveStage && (
            <input
              value={stageNotes}
              onChange={(e) => setStageNotes(e.target.value)}
              placeholder="Notes (optional)"
              className="mt-2 w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
            />
          )}
        </div>
      )}

      {/* SLA Warning */}
      {STAGE_SLA[application.stage] && sla !== 'green' && (
        <div className={`px-5 py-2 text-xs flex items-center gap-1 ${sla === 'red' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
          <AlertTriangle className="w-3.5 h-3.5" />
          {days} days in stage — {sla === 'red' ? 'SLA exceeded' : 'approaching SLA'}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {([['details', 'Details'], ['notes', 'Notes'], ['conditions', 'Conditions'], ['history', 'History'], ['comms', 'Comms'], ['documents', 'Docs'], ['financials', '$']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setTab(key); if (key === 'comms') loadCommunications(); if (key === 'documents') loadSharedDocuments(); if (key === 'financials') loadFinancials() }}
            className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {tab === 'details' && (
          <div className="space-y-4">
            {editingDetails ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Lender</label>
                    <input value={detailForm.lender_name} onChange={(e) => setDetailForm(f => ({...f, lender_name: e.target.value}))} className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Reference</label>
                    <input value={detailForm.lender_reference} onChange={(e) => setDetailForm(f => ({...f, lender_reference: e.target.value}))} className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Loan Amount</label>
                    <input type="text" inputMode="decimal" value={formatNumberWithCommas(detailForm.loan_amount)} onChange={(e) => setDetailForm(f => ({...f, loan_amount: stripToNumber(e.target.value)}))} className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Property Value</label>
                    <input type="text" inputMode="decimal" value={formatNumberWithCommas(detailForm.property_value)} onChange={(e) => setDetailForm(f => ({...f, property_value: stripToNumber(e.target.value)}))} className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Property Address</label>
                  <input value={detailForm.property_address} onChange={(e) => setDetailForm(f => ({...f, property_address: e.target.value}))} className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Purpose</label>
                    <input value={detailForm.loan_purpose} onChange={(e) => setDetailForm(f => ({...f, loan_purpose: e.target.value}))} className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Settlement Date</label>
                    <input type="date" value={detailForm.settlement_date} onChange={(e) => setDetailForm(f => ({...f, settlement_date: e.target.value}))} className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mt-1" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={detailForm.lmi_required} onChange={(e) => setDetailForm(f => ({...f, lmi_required: e.target.checked}))} />
                  LMI Required
                </label>
                <div className="flex gap-2">
                  <button onClick={handleSaveDetails} disabled={loading} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">Save</button>
                  <button onClick={() => setEditingDetails(false)} className="px-4 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
                </div>
              </>
            ) : (
              <>
                {/* Customer Details */}
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Customer Details</h3>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Mail className="w-4 h-4" /> Email
                  </div>
                  <div className="font-medium text-gray-900">{application.customer_email || '—'}</div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <Phone className="w-4 h-4" /> Phone
                  </div>
                  <div className="font-medium text-gray-900">{application.customer_phone || '—'}</div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <MapPin className="w-4 h-4" /> Address
                  </div>
                  <div className="font-medium text-gray-900">{application.customer_address || '—'}</div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <Briefcase className="w-4 h-4" /> Employment
                  </div>
                  <div className="font-medium text-gray-900">{application.customer_employment || '—'}</div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <DollarSign className="w-4 h-4" /> Income
                  </div>
                  <div className="font-medium text-gray-900">{formatCurrency(application.customer_income ?? null)}</div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <Users className="w-4 h-4" /> Dependants
                  </div>
                  <div className="font-medium text-gray-900">{application.customer_dependants ?? '—'}</div>
                </div>

                {/* Loan Details */}
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-5 mb-2">Loan Details</h3>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Building2 className="w-4 h-4" /> Lender
                  </div>
                  <div className="font-medium text-gray-900">{application.lender_name || '—'}</div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <FileText className="w-4 h-4" /> Reference
                  </div>
                  <div className="font-medium text-gray-900">{application.lender_reference || '—'}</div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <DollarSign className="w-4 h-4" /> Loan Amount
                  </div>
                  <div className="font-medium text-gray-900">{formatCurrency(application.loan_amount)}</div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <DollarSign className="w-4 h-4" /> Property Value
                  </div>
                  <div className="font-medium text-gray-900">{formatCurrency(application.property_value)}</div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <MapPin className="w-4 h-4" /> Address
                  </div>
                  <div className="font-medium text-gray-900">{application.property_address || '—'}</div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <FileText className="w-4 h-4" /> Purpose
                  </div>
                  <div className="font-medium text-gray-900">{application.loan_purpose || '—'}</div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <Calendar className="w-4 h-4" /> Settlement
                  </div>
                  <div className="font-medium text-gray-900">
                    {application.settlement_date ? new Date(application.settlement_date).toLocaleDateString() : '—'}
                  </div>

                  <div className="flex items-center gap-2 text-gray-500">
                    <Clock className="w-4 h-4" /> Created
                  </div>
                  <div className="font-medium text-gray-900">
                    {application.created_at ? new Date(application.created_at).toLocaleDateString() : '—'}
                  </div>
                </div>

                {application.lmi_required && (
                  <div className="mt-3 p-2 bg-amber-50 rounded-lg text-xs text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> LMI Required
                    {application.lmi_approved === true && <span className="ml-1 text-green-600 font-medium">— Approved</span>}
                    {application.lmi_approved === false && <span className="ml-1 text-red-600 font-medium">— Declined</span>}
                  </div>
                )}

                <button
                  onClick={() => setEditingDetails(true)}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Edit Details
                </button>
              </>
            )}
          </div>
        )}

        {tab === 'notes' && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Private notes about this client — only visible to you.</p>
            <textarea
              value={brokerNotes}
              onChange={(e) => { setBrokerNotes(e.target.value); setNotesSaved(false) }}
              placeholder="Add your notes about this client..."
              rows={10}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 resize-y"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveNotes}
                disabled={loading}
                className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" />
                Save Notes
              </button>
              {notesSaved && (
                <span className="text-xs text-green-600 font-medium">Saved</span>
              )}
            </div>
          </div>
        )}

        {tab === 'conditions' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                value={newCondition}
                onChange={(e) => setNewCondition(e.target.value)}
                placeholder="Add a condition..."
                className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                onKeyDown={(e) => e.key === 'Enter' && handleAddCondition()}
              />
              <button
                onClick={handleAddCondition}
                disabled={!newCondition.trim()}
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {conditions.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">No conditions added yet</p>
            )}
            {conditions.map((c) => (
              <div key={c.id} className={`flex items-start gap-2 p-3 rounded-lg border ${c.is_cleared ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                <button
                  onClick={() => !c.is_cleared && handleClearCondition(c.id)}
                  disabled={c.is_cleared}
                  className="mt-0.5 flex-shrink-0"
                >
                  {c.is_cleared ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-400 hover:text-green-600" />
                  )}
                </button>
                <div className="min-w-0">
                  <p className={`text-sm ${c.is_cleared ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                    {c.description}
                  </p>
                  {c.cleared_at && (
                    <p className="text-xs text-green-600 mt-1">
                      Cleared {new Date(c.cleared_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'history' && (
          <div className="space-y-0">
            {history.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">No history yet</p>
            )}
            {history.map((entry, i) => (
              <div key={entry.id} className="flex gap-3 pb-4 relative">
                {/* Timeline line */}
                {i < history.length - 1 && (
                  <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-gray-200" />
                )}
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ChevronRight className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {entry.from_stage && (
                      <span className="text-xs text-gray-500">{STAGE_DISPLAY[entry.from_stage] || entry.from_stage}</span>
                    )}
                    {entry.from_stage && <span className="text-xs text-gray-400">&rarr;</span>}
                    <span className="text-xs font-medium text-gray-900">{STAGE_DISPLAY[entry.to_stage] || entry.to_stage}</span>
                  </div>
                  {entry.notes && <p className="text-xs text-gray-600 mt-0.5">{entry.notes}</p>}
                  <p className="text-xs text-gray-400 mt-0.5">
                    {entry.changed_at ? new Date(entry.changed_at).toLocaleString() : ''}
                    {entry.changed_by_name && ` — ${entry.changed_by_name}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'documents' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-sm">Shared Documents</h3>
            {!docsLoaded ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : sharedDocs.length === 0 ? (
              <p className="text-sm text-gray-500">No documents shared by this customer yet.</p>
            ) : (
              <div className="divide-y border rounded-lg">
                {sharedDocs.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{doc.file_name}</p>
                      <p className="text-xs text-gray-500">
                        {DOCUMENT_TYPE_LABELS[doc.document_type as DocumentType] || doc.document_type}
                        {' · '}
                        {doc.file_size < 1024 * 1024
                          ? `${(doc.file_size / 1024).toFixed(0)} KB`
                          : `${(doc.file_size / (1024 * 1024)).toFixed(1)} MB`}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDocDownload(doc)}
                      className="ml-3 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'comms' && (
          <div className="space-y-0">
            {communications.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">No communications sent yet</p>
            )}
            {communications.map((comm, i) => {
              const templateLabels: Record<string, string> = {
                welcome: 'Welcome Email',
                doc_checklist: 'Document Checklist',
                submitted: 'Application Submitted',
                pre_approval: 'Pre-Approval Obtained',
                conditional_approval: 'Conditional Approval',
                unconditional_approval: 'Unconditional Approval',
                sign_documents: 'Loan Documents Ready',
                settlement_approaching: 'Settlement Approaching',
                settled_congratulations: 'Settlement Congratulations',
                declined: 'Application Declined',
                on_hold: 'Application On Hold',
                doc_complete: 'Documents Complete',
                doc_reminder: 'Document Reminder',
                pre_approval_expiring: 'Pre-Approval Expiring',
              }
              const statusColors: Record<string, string> = {
                sent: 'bg-green-100 text-green-700',
                pending: 'bg-yellow-100 text-yellow-700',
                failed: 'bg-red-100 text-red-700',
                retrying: 'bg-orange-100 text-orange-700',
              }
              const brokerEmail = (comm.payload as Record<string, string>)?.broker_email
              return (
                <div key={comm.id} className="flex gap-3 pb-4 relative">
                  {i < communications.length - 1 && (
                    <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-gray-200" />
                  )}
                  <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Mail className="w-3.5 h-3.5 text-indigo-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-gray-900">
                        {templateLabels[comm.template_name] || comm.template_name}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusColors[comm.status] || 'bg-gray-100 text-gray-600'}`}>
                        {comm.status}
                      </span>
                    </div>
                    {comm.subject && (
                      <p className="text-xs text-gray-600 mt-0.5">{comm.subject}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      To: {comm.recipient_email}
                      {brokerEmail && <span className="ml-2">CC: {brokerEmail}</span>}
                    </p>
                    <p className="text-xs text-gray-400">
                      {comm.sent_at
                        ? `Sent ${new Date(comm.sent_at).toLocaleString()}`
                        : comm.created_at
                          ? `Queued ${new Date(comm.created_at).toLocaleString()}`
                          : ''}
                      {comm.attempts > 1 && ` (${comm.attempts} attempts)`}
                    </p>
                    {comm.last_error && (
                      <p className="text-xs text-red-500 mt-0.5">{comm.last_error}</p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {tab === 'financials' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-sm">Financial Profile</h3>
            {!financialsLoaded ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : !financials || !financials.shared ? (
              <div className="text-center py-8">
                <Lock className="w-8 h-8 mx-auto text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">Customer has not shared their financial profile yet.</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-600 font-medium">Monthly Income</p>
                    <p className="text-lg font-bold text-green-700">{formatCurrency(financials.summary.total_monthly_income)}</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-xs text-red-600 font-medium">Monthly Expenses</p>
                    <p className="text-lg font-bold text-red-700">{formatCurrency(financials.summary.total_monthly_expenses)}</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-600 font-medium">Monthly Liabilities</p>
                    <p className="text-lg font-bold text-amber-700">{formatCurrency(financials.summary.total_monthly_liabilities)}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${financials.summary.net_monthly_cashflow >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <p className="text-xs text-gray-600 font-medium">Net Cash Flow</p>
                    <p className={`text-lg font-bold ${financials.summary.net_monthly_cashflow >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(financials.summary.net_monthly_cashflow)}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium">Total Assets</p>
                    <p className="text-lg font-bold text-blue-700">{formatCurrency(financials.summary.total_assets)}</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs text-purple-600 font-medium">Net Worth</p>
                    <p className={`text-lg font-bold ${financials.summary.net_worth >= 0 ? 'text-purple-700' : 'text-red-700'}`}>
                      {formatCurrency(financials.summary.net_worth)}
                    </p>
                  </div>
                </div>

                {/* Income */}
                {financials.income.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Income ({financials.income.length})</h4>
                    <div className="space-y-1">
                      {financials.income.map(inc => (
                        <div key={inc.id} className="flex justify-between items-center bg-green-50 rounded px-3 py-2 text-sm">
                          <span className="capitalize text-gray-900">{inc.income_type.replace('_', ' ')}{inc.employer_name ? ` (${inc.employer_name})` : ''}</span>
                          <span className="text-green-700 font-medium">{formatCurrency(inc.amount)} {inc.frequency}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expenses */}
                {financials.expenses.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Expenses ({financials.expenses.length})</h4>
                    <div className="space-y-1">
                      {financials.expenses.map(exp => (
                        <div key={exp.id} className="flex justify-between items-center bg-red-50 rounded px-3 py-2 text-sm">
                          <span className="capitalize text-gray-900">{exp.expense_type.replace('_', ' ')}{exp.description ? ` (${exp.description})` : ''}</span>
                          <span className="text-red-700 font-medium">{formatCurrency(exp.amount)} {exp.frequency}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Liabilities */}
                {financials.liabilities.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Liabilities ({financials.liabilities.length})</h4>
                    <div className="space-y-1">
                      {financials.liabilities.map(lia => (
                        <div key={lia.id} className="bg-amber-50 rounded px-3 py-2 text-sm">
                          <div className="flex justify-between items-center">
                            <span className="capitalize text-gray-900">{lia.liability_type.replace('_', ' ')}{lia.lender_name ? ` (${lia.lender_name})` : ''}</span>
                            <span className="text-amber-700 font-medium">{formatCurrency(lia.outstanding_balance)}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {lia.monthly_repayment ? `${formatCurrency(lia.monthly_repayment)}/mo` : ''}
                            {lia.interest_rate ? ` · ${lia.interest_rate}%` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Assets */}
                {financials.assets.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Assets ({financials.assets.length})</h4>
                    <div className="space-y-1">
                      {financials.assets.map(ast => (
                        <div key={ast.id} className="flex justify-between items-center bg-blue-50 rounded px-3 py-2 text-sm">
                          <div>
                            <span className="capitalize text-gray-900">{ast.asset_type.replace('_', ' ')}{ast.institution_name ? ` (${ast.institution_name})` : ''}</span>
                            {ast.address && <p className="text-xs text-gray-500">{ast.address}</p>}
                          </div>
                          <span className="text-blue-700 font-medium">{formatCurrency(ast.estimated_value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
